import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Stethoscope,
  Building2,
  MapPin,
  Loader2,
  Users,
  AlertTriangle,
  CheckCircle,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const createIcon = (color) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="
    background-color: ${color};
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

const professionalIcon = createIcon('#E53935');
const facilityIcon = createIcon('#1976D2');
const pharmacyIcon = createIcon('#4CAF50');
const hospitalIcon = createIcon('#9C27B0');

// Cameroon bounds
const CAMEROON_CENTER = [5.9631, 10.1591];
const CAMEROON_BOUNDS = [
  [1.6546, 8.4944],  // Southwest
  [13.0833, 16.1944] // Northeast
];

// Legend component
const MapLegend = ({ professionals, facilities }) => (
  <div className="absolute bottom-4 left-4 z-[1000] bg-white rounded-xl shadow-lg p-3 text-xs">
    <h4 className="font-semibold text-slate-700 mb-2">Légende</h4>
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-[#E53935] border-2 border-white shadow-sm" />
        <span className="text-slate-600">Professionnels ({professionals})</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-[#1976D2] border-2 border-white shadow-sm" />
        <span className="text-slate-600">Cliniques/Centres ({facilities})</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-[#4CAF50] border-2 border-white shadow-sm" />
        <span className="text-slate-600">Pharmacies</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-[#9C27B0] border-2 border-white shadow-sm" />
        <span className="text-slate-600">Hôpitaux</span>
      </div>
    </div>
  </div>
);

// Coverage region markers
const CoverageMarker = ({ region, data }) => {
  const statusColors = {
    good: '#10B981',
    low: '#F59E0B',
    desert: '#EF4444'
  };
  
  return (
    <CircleMarker
      center={data.coordinates}
      radius={Math.max(10, Math.min(30, data.total * 2))}
      pathOptions={{
        color: statusColors[data.status],
        fillColor: statusColors[data.status],
        fillOpacity: 0.3,
        weight: 2
      }}
    >
      <Popup>
        <div className="text-center">
          <h4 className="font-semibold text-slate-900">{region}</h4>
          <div className="text-sm text-slate-600 mt-1">
            <p>{data.professionals} professionnels</p>
            <p>{data.facilities} établissements</p>
          </div>
          <Badge className={`mt-2 text-xs ${
            data.status === 'good' ? 'bg-emerald-100 text-emerald-700' :
            data.status === 'low' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            {data.status === 'good' ? 'Bonne couverture' :
             data.status === 'low' ? 'Couverture faible' :
             'Désert médical'}
          </Badge>
        </div>
      </Popup>
    </CircleMarker>
  );
};

const AdminMap = () => {
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('all');
  const [professionals, setProfessionals] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [facilityTypeFilter, setFacilityTypeFilter] = useState('all');
  const [specialties, setSpecialties] = useState([]);
  const [facilityTypes, setFacilityTypes] = useState([]);

  useEffect(() => {
    loadMapData();
  }, []);

  const loadMapData = async () => {
    setLoading(true);
    try {
      const [prosRes, facsRes, coverageRes] = await Promise.all([
        adminAPI.getMapProfessionals(),
        adminAPI.getMapFacilities(),
        adminAPI.getMapCoverage()
      ]);
      
      setProfessionals(prosRes.data.professionals || []);
      setFacilities(facsRes.data.facilities || []);
      setCoverage(coverageRes.data.coverage || []);
      setSpecialties(prosRes.data.specialties || []);
      setFacilityTypes(facsRes.data.facility_types || []);
    } catch (error) {
      console.error('Error loading map data:', error);
      toast.error('Erreur lors du chargement de la carte');
    } finally {
      setLoading(false);
    }
  };

  const getFacilityIcon = (type) => {
    switch (type) {
      case 'pharmacy': return pharmacyIcon;
      case 'hospital': return hospitalIcon;
      default: return facilityIcon;
    }
  };

  const filteredProfessionals = specialtyFilter === 'all' 
    ? professionals 
    : professionals.filter(p => p.specialty === specialtyFilter);

  const filteredFacilities = facilityTypeFilter === 'all'
    ? facilities
    : facilities.filter(f => f.facility_type === facilityTypeFilter);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="flex items-center justify-center h-[500px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-2" />
            <p className="text-sm text-slate-500">Chargement de la carte...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-map">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Couverture Géographique
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Répartition des professionnels et établissements au Cameroun
          </p>
        </div>
        
        {/* View Tabs */}
        <Tabs value={activeView} onValueChange={setActiveView} className="w-auto">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="all" className="text-xs">Tout</TabsTrigger>
            <TabsTrigger value="professionals" className="text-xs">Pros</TabsTrigger>
            <TabsTrigger value="facilities" className="text-xs">Établissements</TabsTrigger>
            <TabsTrigger value="coverage" className="text-xs">Couverture</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        {(activeView === 'all' || activeView === 'professionals') && (
          <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
            <SelectTrigger className="w-48 h-9 text-sm">
              <Stethoscope className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Spécialité" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes spécialités</SelectItem>
              {specialties.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {(activeView === 'all' || activeView === 'facilities') && (
          <Select value={facilityTypeFilter} onValueChange={setFacilityTypeFilter}>
            <SelectTrigger className="w-48 h-9 text-sm">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous types</SelectItem>
              {facilityTypes.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <Button variant="outline" size="sm" onClick={loadMapData} className="h-9">
          <Filter className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Map Container */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <CardContent className="p-0 relative">
          <div className="h-[500px] w-full">
            <MapContainer
              center={CAMEROON_CENTER}
              zoom={6}
              style={{ height: '100%', width: '100%' }}
              maxBounds={CAMEROON_BOUNDS}
              minZoom={5}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              {/* Professionals */}
              {(activeView === 'all' || activeView === 'professionals') && 
                filteredProfessionals.map((pro, idx) => (
                  <Marker
                    key={`pro-${pro.id || idx}`}
                    position={[pro.latitude, pro.longitude]}
                    icon={professionalIcon}
                  >
                    <Popup>
                      <div className="min-w-[180px]">
                        <div className="flex items-center gap-2 mb-2">
                          <Stethoscope className="w-4 h-4 text-red-500" />
                          <span className="font-semibold text-slate-900">{pro.full_name || 'Professionnel'}</span>
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p><span className="font-medium">Spécialité:</span> {pro.specialty || '-'}</p>
                          {pro.location && <p><span className="font-medium">Lieu:</span> {pro.location}</p>}
                          <Badge className={pro.is_validated ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {pro.is_validated ? 'Vérifié' : 'En attente'}
                          </Badge>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))
              }
              
              {/* Facilities */}
              {(activeView === 'all' || activeView === 'facilities') &&
                filteredFacilities.map((fac, idx) => (
                  <Marker
                    key={`fac-${fac.id || idx}`}
                    position={[fac.latitude, fac.longitude]}
                    icon={getFacilityIcon(fac.facility_type)}
                  >
                    <Popup>
                      <div className="min-w-[180px]">
                        <div className="flex items-center gap-2 mb-2">
                          <Building2 className="w-4 h-4 text-blue-500" />
                          <span className="font-semibold text-slate-900">{fac.name}</span>
                        </div>
                        <div className="text-sm text-slate-600 space-y-1">
                          <p><span className="font-medium">Type:</span> {fac.facility_type}</p>
                          <p><span className="font-medium">Ville:</span> {fac.city}</p>
                          {fac.emergency_24h && (
                            <Badge className="bg-red-100 text-red-700">Urgences 24h</Badge>
                          )}
                          {fac.coordinates_estimated && (
                            <p className="text-xs text-amber-600 italic">Position approximative</p>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))
              }
              
              {/* Coverage regions */}
              {activeView === 'coverage' &&
                coverage.map((region, idx) => (
                  <CoverageMarker
                    key={`region-${idx}`}
                    region={region.region}
                    data={region}
                  />
                ))
              }
            </MapContainer>
          </div>
          
          {/* Legend */}
          {activeView !== 'coverage' && (
            <MapLegend 
              professionals={filteredProfessionals.length} 
              facilities={filteredFacilities.length} 
            />
          )}
        </CardContent>
      </Card>

      {/* Coverage Stats */}
      {activeView === 'coverage' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {coverage.map((region) => (
            <Card key={region.region} className={`border-0 shadow-sm ${
              region.status === 'desert' ? 'bg-red-50' :
              region.status === 'low' ? 'bg-amber-50' : 'bg-emerald-50'
            }`}>
              <CardContent className="p-3 text-center">
                <p className="text-xs font-medium text-slate-500">{region.region}</p>
                <p className="text-lg font-bold text-slate-900">{region.total}</p>
                <div className="flex items-center justify-center gap-1 mt-1">
                  {region.status === 'desert' ? (
                    <AlertTriangle className="w-3 h-3 text-red-500" />
                  ) : region.status === 'low' ? (
                    <AlertTriangle className="w-3 h-3 text-amber-500" />
                  ) : (
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                  )}
                  <span className={`text-xs ${
                    region.status === 'desert' ? 'text-red-600' :
                    region.status === 'low' ? 'text-amber-600' : 'text-emerald-600'
                  }`}>
                    {region.status === 'desert' ? 'Désert' :
                     region.status === 'low' ? 'Faible' : 'OK'}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMap;
